/**
 * THE DHANABHYAS PRODUCT — the India deployment, served at app.dhanabhyas.com.
 *
 * <h2>⚠ A FOURTH MANIFEST THE BRIEF DID NOT ASK FOR, and why it has to exist</h2>
 *
 * The brief named three: astro, integralDelta, admin. The codebase already carries FOUR product
 * targets — {@code productTarget.js} declares {@code DHANABHYAS} as its own, with the argument
 * stated at the constant: it denies exactly what astro denies today and could have BEEN astro
 * with different branding, and was kept separate so that the first time India wants a study
 * astrostockcharts.com does not offer, the change can be expressed without touching the other
 * site.
 *
 * <p>Without this file that target has no manifest, so {@code resolveInitialManifest} would read
 * {@code productTarget.json}, find {@code 'dhanabhyas'}, fail to match it and fall back to astro
 * — silently collapsing two products the codebase deliberately keeps apart, and doing it on the
 * fallback path where nothing says so. Three manifests for four targets is a hole, not a
 * simplification.
 *
 * <h2>Its toolbar STARTS as a copy of astro's and is free to diverge</h2>
 *
 * Shared by importing astro's own arrays rather than by retyping them, so the two cannot drift
 * while they are meant to agree. The moment one product gains a study the other does not, the
 * spread becomes a literal list here and nothing else changes.
 *
 * <h2>⚠ NEITHER THE MARKET SCANNER NOR EITHER MESSAGE SURFACE</h2>
 *
 * Confirmed absent 2026-11-02, and for the same reasons astro's own docblock gives at length —
 * read it rather than duplicating it here, because two copies of an exclusion rationale is how
 * one of them goes stale.
 *
 * <p>The scanner is the one worth restating: the backend's
 * {@code ProductAudience.allowsIntegralDeltaFeature()} answers a dhanabhyas customer with **403**,
 * so naming it here would put a button on screen that fails on every click. It is on
 * integralDelta's toolbar and nowhere else among the customer products.
 *
 * <h2>⚠ AND NOT `configuration` EITHER, since 2026-11-03</h2>
 *
 * The PTA/PTB/CTS colour dialog, removed on instruction in step with astro. A saved preference is
 * still LOADED — `App` reads it at mount and nothing about that is gated on the toolbar — but it
 * can no longer be changed or cleared, because `ConfigurationDialog` is the only writer and this
 * product has no Admin Panel to reach it through. Astro's docblock carries the full account; it is
 * not repeated here, because two copies of a rationale is how one of them goes stale.
 */

import { CORE_DRAWING, CORE_INDICATORS, CLEAR_ACTIONS } from './astro.manifest';

/*
 * ⚠ `watermark_toggle` IS NOT HERE, and it was until a test caught it.
 *
 * The control is `props.isAdmin &&` gated in the toolbar, so a customer manifest naming it
 * claimed a capability the role gate withholds — a manifest that is not the product definition,
 * which is the one thing this architecture may not be. It lives in the admin manifest alone.
 */
const VIEW_CHROME = Object.freeze([
  'chart_type',
  'area_zoom',
  'save_screenshot',
  'copy_chart',
  /*
   * ⚠ THE STUDY-OUTPUT TOGGLE — see the note in `astro.manifest.js`. Written out here rather
   * than imported, like the rest of this list, so the India product can diverge from astro
   * without touching it.
   *
   * <p>A second route to the drawer; `MessagesPanelButton` stays in the header on all four
   * products. The control itself is hand-written JSX in `TabPanel` gated by `showsTool` —
   * `messages_panel` has no `ICONS` row, so naming it here alone would admit nothing.
   */
  'messages_panel',
]);

export const dhanabhyasManifest = Object.freeze({
  id: 'dhanabhyas',

  /**
   * ⚠ `Dhanabhyas.com` WITH the suffix, where astro is `Dhanabhyas` without it.
   *
   * That is the existing contract — {@code nonAdminChrome.js} returns exactly these two strings
   * and already notes that they differ only by the suffix and that the difference is left as it
   * is. Reproduced rather than tidied: changing either changes what a live customer's header
   * says, and the manifest refactor is not the place to make that decision.
   */
  brandName: 'Dhanabhyas.com',
  watermark: 'Dhanabhyas.com',

  domains: Object.freeze([
    'app.dhanabhyas.com',
    'dhanabhyas.com',
    'www.dhanabhyas.com',
  ]),

  /*
   * ⚠ A SET, NOT AN ORDER — see astro's own note. `TabPanel` renders `ICONS.map(...)` and reads
   * this only through `showsAction`, so the order here is for a reader of this file.
   */
  toolbar: Object.freeze([
    'planetary_lines',
    'average_longitude',
    'longitude_study',
    'declination_study',
    'degree_range_highlight',
    'area_highlight',
    'planet_follow',
    'planet_characteristics',

    /*
     * The four strategy studies — ADDED 2026-11-03, in step with astro, whose docblock carries the
     * reasoning. Kept as a literal list here rather than imported: these two products are meant to
     * be free to diverge, and a shared array would make the next India-only decision a change to
     * astro's file.
     */
    'strategy_1',
    'strategy_2',
    'time_event_strategy',
    'moa_strategy',

    'astro_catalog_notes',
    'intraday_animation',

    ...CORE_DRAWING,

    /*
     * ⚠ RESTORED 2026-11-01, having left the bar in Stage 1 as a consequence of the brief's
     * explicit tool lists. It is standard chart functionality — zoom in, click the two bars that
     * define a cycle, and the dialog draws it — not a research instrument, so withholding it from
     * a customer was the allow-list being read more literally than it was meant.
     */
    'cycles_dialog',
    ...CORE_INDICATORS,
    ...VIEW_CHROME,
    ...CLEAR_ACTIONS,
  ]),

  adminPanel: null,
});

export default dhanabhyasManifest;
